import React from 'react';

const ITMEPoliciesFramework = () => {
    return (
        <div style={styles.page}>
            <h1 style={styles.header}>ITME Policies</h1>
            <p style={styles.intro}>
                <em>Innovative Trends in Multidisciplinary Engineering (ITME)</em> is committed to maintaining the highest standards of academic integrity, transparency, and ethical responsibility in scholarly publishing. The journal follows internationally accepted principles of ethical publishing and aims to promote responsible dissemination of scientific knowledge in multidisciplinary engineering domains including mechanical, civil, electrical, electronics, computer science, and emerging interdisciplinary technologies.
            </p>
            <p style={styles.intro}>
                These policies define the ethical responsibilities and expectations of authors, reviewers, editors, and the publisher and provide a transparent framework governing manuscript submission, peer review, publication decisions, corrections, and retractions. The policies are designed to align with widely accepted international scholarly publishing standards and good editorial practice.
            </p>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>1. Peer Review Policy</h2>
                <p>
                    ITME follows a rigorous double‑blind peer review system to ensure fairness, academic rigor, and unbiased evaluation of submitted manuscripts. In the double‑blind review model, the identities of both authors and reviewers remain concealed throughout the review process. This practice ensures that evaluation is conducted solely on the scientific merit of the work without influence from institutional affiliations, nationality, or academic seniority.
                </p>
                <p>
                    The peer review process begins with an initial editorial screening conducted by the editorial office. This stage evaluates whether the manuscript falls within the scope of the journal, meets minimum quality standards, and follows the journal's formatting and submission guidelines. Manuscripts failing to meet these criteria may be returned to authors for technical corrections or rejected prior to external review.
                </p>
                <p>
                    Manuscripts that successfully pass editorial screening are assigned to a Handling Editor who is a member of the Editorial Board with relevant subject expertise. The Handling Editor identifies appropriate reviewers who possess the required technical knowledge to evaluate the manuscript. Typically, a minimum of two independent reviewers are invited for each submission.
                </p>
                <p>
                    Reviewers assess manuscripts on the basis of originality, methodological rigor, clarity of objectives, appropriateness of research design, validity of results, depth of literature review, and overall contribution to the field of multidisciplinary engineering.
                </p>
                <p>
                    Based on reviewer feedback, the editorial decision may fall into the following categories: Accept, Minor Revision, Major Revision, or Reject. Authors receiving revision decisions must submit a revised manuscript along with a detailed response to reviewers explaining how each comment has been addressed.
                </p>
                <p>
                    The final publication decision is made by the Editor‑in‑Chief after careful consideration of reviewer recommendations and editorial judgment.
                </p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>2. Responsibilities of Authors</h2>
                <p>Authors submitting manuscripts to ITME must ensure that their work represents original research and has not been published previously nor is under consideration for publication elsewhere.</p>
                <p>Authors must provide proper acknowledgment of the work of others and must cite all relevant sources appropriately. Failure to provide adequate citation may constitute plagiarism.</p>
                <p>All listed authors must have made substantial intellectual contributions to the work including conceptualization, methodology, analysis, or writing. Individuals who contributed to the research but do not meet authorship criteria should be acknowledged appropriately.</p>
                <p>Authors must ensure that all data presented in the manuscript are accurate and have not been fabricated or manipulated. Any errors discovered after publication should be promptly communicated to the editorial office for correction.</p>
                <p>Where research involves human participants or animals, authors must confirm that appropriate ethical approvals have been obtained from relevant institutional review boards.</p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>3. Responsibilities of Editors</h2>
                <p>The Editor‑in‑Chief and editorial board members are responsible for maintaining the quality and integrity of the journal. Editorial decisions must be based solely on the scholarly merit of the manuscript and its relevance to the journal's aims and scope.</p>
                <p>Editors must ensure that manuscripts are evaluated fairly and confidentially. They must avoid conflicts of interest and should recuse themselves from handling submissions where such conflicts exist.</p>
                <p>Editors are responsible for selecting qualified reviewers and ensuring that the peer review process is conducted in a timely and professional manner.</p>
                <p>Editors must also ensure that all ethical concerns raised during the review process are investigated appropriately and resolved according to established publication ethics guidelines.</p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>4. Responsibilities of Reviewers</h2>
                <p>Reviewers play a critical role in maintaining the scholarly quality of the journal. They are expected to provide objective, constructive, and timely evaluations of manuscripts.</p>
                <p>Reviewers must treat manuscripts as confidential documents and must not share or discuss the content with others without authorization from the editorial office.</p>
                <p>Reviewers should identify relevant published work that has not been cited by the authors and should notify the editor of any substantial similarity between the manuscript under review and other published works.</p>
                <p>If reviewers believe they are not sufficiently qualified to review a manuscript or identify a conflict of interest, they should decline the review invitation.</p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>5. Plagiarism and Academic Misconduct</h2>
                <p>ITME has a strict zero‑tolerance policy toward plagiarism and academic misconduct. All submitted manuscripts are screened using plagiarism detection software prior to peer review.</p>
                <p>Manuscripts with unacceptable similarity levels may be returned to authors for revision or rejected outright depending on the severity of overlap.</p>
                <p>Intentional plagiarism, data falsification, or unethical research conduct may result in manuscript rejection and may also lead to notification of the author's institution.</p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>6. Conflict of Interest Policy</h2>
                <p>Authors, reviewers, and editors must disclose any financial, institutional, or personal relationships that could influence the interpretation or evaluation of research findings.</p>
                <p>Disclosure of conflicts of interest promotes transparency and helps maintain trust in the scholarly publication process.</p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>7. Data Availability and Transparency</h2>
                <p>Authors are encouraged to make research data available whenever possible to facilitate verification and reproducibility of research findings.</p>
                <p>Where datasets cannot be publicly shared due to privacy or confidentiality constraints, authors should clearly state the conditions under which data may be accessed.</p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>8. Corrections, Retractions, and Expressions of Concern</h2>
                <p>The journal is committed to maintaining the accuracy and integrity of the scientific record.</p>
                <p>If significant errors are discovered after publication, the journal may issue corrections, corrigenda, or errata.</p>
                <p>In cases involving serious ethical violations or unreliable findings, the journal may issue a formal retraction notice.</p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>9. Digital Archiving and Preservation</h2>
                <p>ITME is committed to long‑term preservation of published scholarly content.</p>
                <p>The journal ensures that all published articles are archived and preserved through institutional repositories and digital archiving systems to ensure continued accessibility of the scholarly record.</p>
                <p style={{ fontStyle: 'italic', color: '#555', marginTop: '10px' }}>These policies are periodically reviewed and updated by the Editorial Board to reflect evolving standards in scholarly publishing and research integrity.</p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>10. Policy on AI-Generated Content</h2>
                <p>ITME recognizes the growing role of artificial intelligence (AI) tools in academic research and writing. Authors may use AI tools to support language editing or data analysis; however, AI systems must not be listed as authors or co-authors.</p>
                <p>Authors remain fully responsible for the accuracy, originality, and integrity of the content submitted. Any use of AI tools in manuscript preparation should be transparently disclosed in the methods or acknowledgment section.</p>
                <p>AI tools must not be used to fabricate data, generate false references, or produce misleading research findings. Manuscripts suspected of unethical AI usage may be rejected.</p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>11. Human and Animal Research Ethics</h2>
                <p>Research involving human participants must comply with recognized ethical standards and must receive approval from an appropriate institutional ethics committee or review board. Authors should clearly state the approval number and confirm that informed consent was obtained from participants.</p>
                <p>Research involving animals must follow internationally accepted guidelines for ethical animal treatment and must be approved by the relevant institutional ethics authority. Authors must confirm that all procedures minimized suffering and complied with regulatory requirements.</p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>12. Copyright and Licensing (Open Access – CC BY)</h2>
                <p>ITME publishes articles under an open access model to ensure broad dissemination of research findings. Published articles are typically distributed under a Creative Commons Attribution (CC BY) license, which allows unrestricted use, distribution, and reproduction in any medium, provided the original work is properly cited.</p>
                <p>Authors retain copyright of their work while granting the journal the right to publish and distribute the article as part of the journal's scholarly record.</p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>13. Author Appeals and Complaints Procedure</h2>
                <p>Authors who disagree with an editorial decision may submit a formal appeal to the editorial office. The appeal must clearly explain the reasons for disagreement and provide supporting evidence.</p>
                <p>The Editor-in-Chief will review the appeal and may consult additional reviewers or editorial board members if necessary. The decision reached after appeal review will be considered final.</p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>14. Editorial Independence</h2>
                <p>The editorial decision-making process at ITME is independent of institutional, financial, or commercial influence. Editorial decisions are based solely on academic merit, originality, and relevance to the journal's scope.</p>
                <p>The publisher and sponsoring institution do not interfere with editorial evaluations or publication decisions.</p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>15. Submission and Revision Timeline</h2>
                <p>ITME strives to ensure a timely and efficient peer review process. Initial editorial screening typically occurs within one week of submission.</p>
                <p>External peer review generally takes two to three weeks depending on reviewer availability. Authors receiving revision requests are expected to submit revised manuscripts within the specified timeframe indicated in the decision letter.</p>
            </section>

            <section style={{ ...styles.section, borderBottom: 'none' }}>
                <h2 style={styles.sectionHeader}>16. Open Access Policy</h2>
                <p>ITME operates under a full open access publishing model, ensuring that all published articles are freely accessible to readers worldwide without subscription barriers.</p>
                <p>Open access publishing promotes the rapid dissemination of research findings and enhances visibility and citation impact for authors.</p>
                <p style={{ fontStyle: 'italic', color: '#555', marginTop: '10px' }}>The Editorial Board periodically reviews and updates these policies to reflect evolving standards in scholarly publishing and research integrity.</p>
            </section>

            <div style={styles.downloadWrapper}>
                <a href="/ITME_Policies_Framework.docx" download style={styles.downloadBtn}>
                    <span>Download ITME Policies Framework</span>
                    <svg style={styles.downloadIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                </a>
            </div>
        </div>
    );
};

const styles = {
    page: {
        maxWidth: '800px',
        margin: 'auto',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        lineHeight: '1.6',
        color: '#333',
        textAlign: 'justify',
    },
    header: {
        fontSize: '2.2rem',
        background: 'linear-gradient(90deg, #004aad, #0078d7)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textAlign: 'center',
        marginBottom: '20px',
    },
    intro: {
        fontSize: '1.1rem',
        marginBottom: '20px',
        textAlign: 'justify',
    },
    section: {
        marginBottom: '20px',
        borderBottom: '2px solid #e0e0e0',
        paddingBottom: '15px',
    },
    sectionHeader: {
        fontSize: '1.5rem',
        color: '#0078d7',
    },
    downloadWrapper: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '30px',
        marginBottom: '30px',
    },
    downloadBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        padding: '14px 32px',
        background: 'linear-gradient(135deg, #004aad, #0078d7)',
        color: '#fff',
        fontSize: '1.05rem',
        fontWeight: '600',
        borderRadius: '8px',
        textDecoration: 'none',
        transition: 'transform 0.2s, box-shadow 0.2s',
        boxShadow: '0 4px 14px rgba(0, 74, 173, 0.25)',
        cursor: 'pointer',
    },
    downloadIcon: {
        width: '20px',
        height: '20px',
    },
};

export default ITMEPoliciesFramework;
